/**
 * Team Grid: modal open/close with GSAP (backdrop blur + dialog motion).
 */
import gsap from "gsap";

const EASE_OUT = "power3.out";
const EASE_IN = "power3.in";

function lockScroll(lock) {
	document.documentElement.classList.toggle("team-modal-is-open", lock);
	document.body.classList.toggle("team-modal-is-open", lock);
}

export function initTeamGridModals() {
	const roots = document.querySelectorAll("[data-team-modal-root]");
	if (!roots.length) return;

	roots.forEach((root) => {
		const backdrop = root.querySelector(".team-modal__backdrop");
		const dialog = root.querySelector(".team-modal__dialog");
		const closeEls = root.querySelectorAll("[data-team-modal-close]");
		const panels = root.querySelectorAll("[data-team-modal-panel]");
		const grid = root.closest(".team-grid");
		if (!backdrop || !dialog || !grid) return;

		const cards = grid.querySelectorAll(".team-card[data-team-modal]");
		let activePanel = null;
		let openTl = null;
		let closeTl = null;
		let lastFocus = null;

		function hideAllPanels() {
			panels.forEach((p) => {
				p.setAttribute("hidden", "");
				p.classList.remove("is-active");
			});
			activePanel = null;
		}

		function showPanel(id) {
			hideAllPanels();
			const sid = String(id).replace(/\\/g, "\\\\").replace(/"/g, '\\"');
			const panel = root.querySelector(`[data-team-modal-panel="${sid}"]`);
			if (!panel) return;
			panel.removeAttribute("hidden");
			panel.classList.add("is-active");
			activePanel = panel;
			const title = panel.querySelector(".team-modal__name");
			if (title && title.id) {
				dialog.setAttribute("aria-labelledby", title.id);
			}
		}

		function resetModalScroll() {
			dialog.scrollTop = 0;
		}

		function openModal(id) {
			if (openTl) openTl.kill();
			if (closeTl) closeTl.kill();
			showPanel(id);
			if (!activePanel) return;

			lastFocus = document.activeElement;
			root.removeAttribute("hidden");
			lockScroll(true);
			resetModalScroll();

			gsap.set(root, { display: "flex" });
			gsap.set(backdrop, { opacity: 0 });
			gsap.set(dialog, { opacity: 0, y: 36, scale: 0.94 });

			openTl = gsap.timeline({
				onComplete: () => {
					gsap.set(dialog, { clearProps: "transform" });
					resetModalScroll();
					const btn = root.querySelector(".team-modal__close");
					if (btn) btn.focus();
				},
			});
			openTl.to(
				backdrop,
				{
					opacity: 1,
					duration: 0.5,
					ease: EASE_OUT,
				},
				0
			);
			openTl.to(
				dialog,
				{
					opacity: 1,
					y: 0,
					scale: 1,
					duration: 0.55,
					ease: "back.out(1.2)",
				},
				0.08
			);
		}

		function closeModal() {
			if (openTl) openTl.kill();
			if (closeTl) closeTl.kill();

			closeTl = gsap.timeline({
				onComplete: () => {
					root.setAttribute("hidden", "");
					gsap.set(root, { display: "none" });
					gsap.set(dialog, { clearProps: "transform" });
					resetModalScroll();
					hideAllPanels();
					dialog.removeAttribute("aria-labelledby");
					lockScroll(false);
					if (lastFocus && typeof lastFocus.focus === "function") {
						lastFocus.focus();
					}
					lastFocus = null;
				},
			});

			closeTl.to(
				dialog,
				{
					opacity: 0,
					y: 28,
					scale: 0.96,
					duration: 0.35,
					ease: EASE_IN,
				},
				0
			);
			closeTl.to(
				backdrop,
				{
					opacity: 0,
					duration: 0.4,
					ease: EASE_IN,
				},
				0.05
			);
		}

		cards.forEach((card) => {
			card.addEventListener("click", () => {
				const id = card.getAttribute("data-team-modal");
				if (id) openModal(id);
			});
		});

		closeEls.forEach((el) => {
			el.addEventListener("click", (e) => {
				e.preventDefault();
				closeModal();
			});
		});

		document.addEventListener("keydown", (e) => {
			if (e.key !== "Escape") return;
			if (root.hasAttribute("hidden")) return;
			closeModal();
		});
	});
}
